import { useSearchEnrollments } from '@actions';
import { PaginationProps } from '@components/V4/Pagination';
import { SortOrder } from '@components/V4/Table/Table';
import { useDebounce, useQueryParamsState } from '@hooks';
import DetailsModal, {
  DetailsModalPage,
} from '@modules/Admin/components/DetailsModal/DetailsModal';
import { TriState } from '@shared/common';
import { getValidMoment } from 'apps/agora/src/utils/helpers';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import EnrollmentsHeader from './components/EnrollmentsHeader';
import EnrollmentsTable from './components/EnrollmentsTable';

const useQuery = () => new URLSearchParams(useLocation().search);

const Enrollments = () => {
  const [enrollmentId, setEnrollmentId] = useState<string>();

  const [selectedEnrollments, setSelectedEnrollments] = useState<string[]>([]);

  const [mentorId, setMentorId] = useState<string>();
  const [studentId, setStudentId] = useState<string>();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalPage, setModalPage] = useState<DetailsModalPage>('student');

  const [studentSearch, setStudentSearch] = useState('');
  const [mentorSearch, setMentorSearch] = useState('');

  const debouncedStudentSearch = useDebounce(studentSearch, 500);
  const debouncedMentorSearch = useDebounce(mentorSearch, 500);

  const query = useQuery();
  const limit = query.get('limit') || '10';
  const page = query.get('page') || '1';
  const startDateRangeStart = getValidMoment(query.get('startDateRangeStart'));
  const startDateRangeEnd = getValidMoment(query.get('startDateRangeEnd'));
  const endDateRangeStart = getValidMoment(query.get('endDateRangeStart'));
  const endDateRangeEnd = getValidMoment(query.get('endDateRangeEnd'));
  const course = query.get('course') || null;
  const student = query.get('student') || null;
  const mentor = query.get('mentor') || null;
  const stage = query.get('stage') || null;
  const status = query.get('status') || null;
  const urgency = query.get('urgency') || null;
  const sortBy = query.get('sortBy') || null;
  const sortOrder = (query.get('sortOrder') || null) as SortOrder;
  const graduationYears = query.get('graduationYears')
    ? query.get('graduationYears')?.split(',')
    : null;
  const completedSessionsMin = query.get('completedSessionsMin') || null;
  const completedSessionsMax = query.get('completedSessionsMax') || null;

  const hideEnrollmentsWithNoSessions = (query.get('hideEnrollmentsWithNoSessions') ||
    'unchecked') as TriState;
  const showMentorsAsStudents = (query.get('showMentorsAsStudents') || 'checked') as TriState;
  const showDeactivatedStudents = (query.get('showDeactivatedStudents') || 'unchecked') as TriState;

  const [searchParams, setSearchParams] = useQueryParamsState<any>({
    page: page,
    limit: limit,
    startDateRangeStart: startDateRangeStart,
    startDateRangeEnd: startDateRangeEnd,
    endDateRangeStart: endDateRangeStart,
    endDateRangeEnd: endDateRangeEnd,
    course: course,
    student: student,
    mentor: mentor,
    stage: stage,
    status: status,
    urgency: urgency,
    sortBy: sortBy,
    sortOrder: sortOrder,
    graduationYears: graduationYears,
    completedSessionsMin: completedSessionsMin,
    completedSessionsMax: completedSessionsMax,
    hideEnrollmentsWithNoSessions: hideEnrollmentsWithNoSessions,
    showMentorsAsStudents: showMentorsAsStudents,
    showDeactivatedStudents: showDeactivatedStudents,
  });

  const isCompletedSessionsRangeInvalid =
    !!searchParams.completedSessionsMin &&
    !!searchParams.completedSessionsMax &&
    Number(searchParams.completedSessionsMin) > Number(searchParams.completedSessionsMax);

  const { data: enrollments, isLoading: areEnrollmentsLoading } = useSearchEnrollments({
    page: searchParams.page,
    limit: searchParams.limit,
    startDateRangeStart: searchParams.startDateRangeStart,
    startDateRangeEnd: searchParams.startDateRangeEnd,
    endDateRangeStart: searchParams.endDateRangeStart,
    endDateRangeEnd: searchParams.endDateRangeEnd,
    course: searchParams.course,
    student: searchParams.student,
    mentor: searchParams.mentor,
    stage: searchParams.stage && searchParams.stage.split(','),
    status: searchParams.status && searchParams.status.split(','),
    urgency: searchParams.urgency,
    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder,
    graduationYears: searchParams.graduationYears,
    completedSessionsMin:
      searchParams.completedSessionsMin && !isCompletedSessionsRangeInvalid
        ? Number(searchParams.completedSessionsMin)
        : undefined,
    completedSessionsMax:
      searchParams.completedSessionsMax && !isCompletedSessionsRangeInvalid
        ? Number(searchParams.completedSessionsMax)
        : undefined,
    hideEnrollmentsWithNoSessions: searchParams.hideEnrollmentsWithNoSessions,
    showMentorsAsStudents: searchParams.showMentorsAsStudents,
    showDeactivatedStudents: searchParams.showDeactivatedStudents,
  });

  const currentPageChangeHandler = (page: number) => {
    setSearchParams({
      page,
    });
  };

  const pageSizeChangeHandler = (size: number) => {
    setSearchParams({
      limit: size,
    });
  };

  const mentorModalOpenHandler = (mentorId: string) => {
    setModalPage('mentor');
    setMentorId(mentorId);
    setShowDetailsModal(true);
  };

  const enrollmentModalOpenHandler = (enrollmentId: string) => {
    setModalPage('enrollment');
    setEnrollmentId(enrollmentId);
    setShowDetailsModal(true);
  };

  const enrollmentsModalOpenHandler = () => {
    setModalPage('enrollments');
    setShowDetailsModal(true);
  };

  const openStudentDetailsModalHandler = (studentId: string) => {
    setStudentId(studentId);
    setShowDetailsModal(true);
  };

  const closeSessionModalHandler = () => {
    setStudentId(undefined);
    setEnrollmentId(undefined);
    setShowDetailsModal(false);
    setModalPage(undefined);
  };

  const pagination: PaginationProps = {
    pageSize: limit !== null ? parseInt(limit) : 10,
    total: enrollments?.total,
    currentPage: page !== null ? parseInt(page) : 1,
    onCurrentPageChange: currentPageChangeHandler,
    onPageSizeChange: pageSizeChangeHandler,
  };

  return (
    <section className="flex flex-col gap-8">
      <EnrollmentsHeader
        createdByOptions={enrollments?.filters?.users}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        studentSearch={studentSearch}
        setStudentSearch={setStudentSearch}
        mentorSearch={mentorSearch}
        setMentorSearch={setMentorSearch}
        graduationYears={graduationYears || []}
        isCompletedSessionsRangeInvalid={isCompletedSessionsRangeInvalid}
        debouncedStudentSearch={debouncedStudentSearch}
        debouncedMentorSearch={debouncedMentorSearch}
        hideEnrollmentsWithNoSessions={hideEnrollmentsWithNoSessions}
        showMentorsAsStudents={showMentorsAsStudents}
        showDeactivatedStudents={showDeactivatedStudents}
        selectedEnrollments={selectedEnrollments}
        setSelectedEnrollments={setSelectedEnrollments}
      />
      <EnrollmentsTable
        enrollments={enrollments?.enrollments}
        areEnrollmentsLoading={areEnrollmentsLoading}
        pagination={pagination}
        sortBy={searchParams.sortBy}
        sortOrder={searchParams.sortOrder}
        setSearchParams={setSearchParams}
        mentorModalOpenHandler={mentorModalOpenHandler}
        enrollmentModalOpenHandler={enrollmentModalOpenHandler}
        openStudentDetailsModalHandler={openStudentDetailsModalHandler}
        setSelectedEnrollments={setSelectedEnrollments}
        enrollmentsModalOpenHandler={enrollmentsModalOpenHandler}
        selectedEnrollments={selectedEnrollments}
      />
      {showDetailsModal && (
        <DetailsModal
          mentorId={mentorId}
          studentId={studentId}
          enrollmentId={enrollmentId}
          enrollmentsIds={selectedEnrollments}
          page={modalPage}
          isOpen={true}
          onClose={closeSessionModalHandler}
        />
      )}
    </section>
  );
};

export default Enrollments;
